import { useCallback, useRef } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type {
  ProviderAgreement,
  ProviderAgreementInput,
  ProviderRate,
  ProviderRateInput,
  ProviderRateUpdate,
} from '@/types/provider-cost';
import { toError } from '@/utils/errors';
import { UNCHANGED, usePoll, withEtag } from './usePoll';

/** The rate card changes when someone edits it, not by the minute. */
export const PROVIDER_COSTS_POLL_MS = 60_000;

export interface ProviderCostsQuery {
  provider?: string;
  /** Only rates in effect on this ISO date; leave out for full history. */
  activeOn?: string;
}

/**
 * The provider rate card and the prepaid agreements behind it, polled with
 * ETag revalidation, with the writes that change them.
 *
 * Every write re-reads both lists, whether it succeeds or not: saving an
 * agreement rewrites its rates on the card, a new rate can close an older
 * one, and a refused write (a 409) means what is on screen is out of date.
 * Writes reject with the server's message so a form can show it.
 */
export function useProviderCosts(query: ProviderCostsQuery = {}) {
  const key = JSON.stringify(query);
  const queryRef = useRef(query);
  queryRef.current = query;

  const rates = usePoll<ProviderRate[]>({
    interval: PROVIDER_COSTS_POLL_MS,
    refetchDeps: [key],
    queryFn: async ({ signal, etag, manual }) => {
      const result = await vnextApiClient.listProviderRates(queryRef.current, {
        signal,
        etag: manual ? null : etag,
      });
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });

  const agreements = usePoll<ProviderAgreement[]>({
    interval: PROVIDER_COSTS_POLL_MS,
    // Agreements have no date filter: only a provider change is a new set.
    refetchDeps: [query.provider],
    queryFn: async ({ signal, etag, manual }) => {
      const result = await vnextApiClient.listProviderAgreements(
        { provider: queryRef.current.provider },
        { signal, etag: manual ? null : etag }
      );
      return result === 'not-modified' ? UNCHANGED : withEtag(result.data, result.etag);
    },
    onError: (err) => Sentry.captureException(toError(err)),
  });

  const refetchRates = rates.refetch;
  const refetchAgreements = agreements.refetch;
  const refetch = useCallback(async () => {
    await Promise.all([refetchRates(), refetchAgreements()]);
  }, [refetchRates, refetchAgreements]);

  const write = useCallback(
    async <T>(action: () => Promise<T>): Promise<T> => {
      try {
        return await action();
      } finally {
        await refetch();
      }
    },
    [refetch]
  );

  const createRate = useCallback(
    (input: ProviderRateInput) => write(() => vnextApiClient.createProviderRate(input)),
    [write]
  );
  const updateRate = useCallback(
    (id: string, update: ProviderRateUpdate) =>
      write(() => vnextApiClient.updateProviderRate(id, update)),
    [write]
  );
  const deleteRate = useCallback(
    (id: string) => write(() => vnextApiClient.deleteProviderRate(id)),
    [write]
  );
  const createAgreement = useCallback(
    (input: ProviderAgreementInput) => write(() => vnextApiClient.createProviderAgreement(input)),
    [write]
  );
  const replaceAgreement = useCallback(
    (id: string, input: ProviderAgreementInput) =>
      write(() => vnextApiClient.replaceProviderAgreement(id, input)),
    [write]
  );
  const deleteAgreement = useCallback(
    (id: string) => write(() => vnextApiClient.deleteProviderAgreement(id)),
    [write]
  );

  const error = rates.error ?? agreements.error;
  return {
    rates: rates.data ?? [],
    agreements: agreements.data ?? [],
    isLoading: rates.isLoading || agreements.isLoading,
    error: error === undefined ? null : toError(error),
    refetch,
    createRate,
    updateRate,
    deleteRate,
    createAgreement,
    replaceAgreement,
    deleteAgreement,
  };
}
